import React, {
	createContext,
	FC,
	ReactNode,
	useContext,
	useMemo,
} from "react";

import { Formik } from "formik";

import { useQuery, UseQueryResult } from "@tanstack/react-query";

import ReportsServices from "@/services/ReportsService";

import { useFiltersContext } from "@/hooks/useFiltersContext";
import { useUserSettings } from "@/hooks/useUserSettings";

import { reportKeys } from "@/constants/CQueryKeys";

import {
	TCompany,
	TReportAPIResponse,
	TReportFormProject,
	TReportFormValues,
	TReportProject,
	TSavedReport,
} from "@/types";

import { mergeTasks } from "@/utils";

interface IReportFormContext {
	reportId?: number;
	existingReport?: TSavedReport;
	isFinalized: boolean;
	tasksQuery: UseQueryResult<TReportAPIResponse>;
}

const ReportFormContext = createContext<IReportFormContext | null>(null);

export const useReportForm = () => {
	const context = useContext(ReportFormContext);
	if (!context) {
		throw new Error("useReportForm must be used within a ReportForm");
	}
	return context;
};

const calcDays = (minutes: number, hoursPerDay?: string) => {
	if (!hoursPerDay || hoursPerDay === "00:00") return "";

	const [h, m] = hoursPerDay.split(":");
	const totalMinutes = parseInt(h || "0", 10) * 60 + parseInt(m || "0", 10);

	return (totalMinutes ? minutes / totalMinutes : 0).toFixed(2);
};

/** The API's days when it could compute them (every author has a rhythm), else the reader's own. */
const daysOf = (project: TReportProject, hoursPerDay?: string) =>
	project.days !== null && project.days !== undefined
		? project.days.toFixed(2)
		: calcDays(project.workedMinutes, hoursPerDay);

const genInitValues = (
	data: TReportAPIResponse | undefined,
	report: TSavedReport | undefined,
	hoursPerDay?: string,
	client?: TCompany | null
): TReportFormValues => {
	const projects: TReportFormProject[] = report?.sections?.length
		? report.sections.map((section) => ({
				name: section.name,
				ticketPrefixes: section.ticketPrefixes ?? [],
				// Saved with the section since; an older report scoped on
				// one project still has that project's.
				color: section.color ?? report.project?.color,
				tasks: section.tasks.map((text) => ({
					id: crypto.randomUUID(),
					text,
				})),
				selected: true,
				days: section.days?.toString() ?? "0",
				initialDays: section.days?.toString() ?? "0",
				editing: false,
				workTime: "",
			}))
		: (data?.projects?.map((project: TReportProject) => ({
				...project,
				tasks: mergeTasks(project.tasks).map((task) => ({
					id: crypto.randomUUID(),
					text: task.name,
				})),
				selected: true,
				days: daysOf(project, hoursPerDay),
				initialDays: daysOf(project, hoursPerDay),
				editing: false,
			})) ?? []);

	// Who is billed: what the saved report says, else the client the
	// report is scoped on, else the client of the one project chosen.
	const scopedClient =
		client ??
		(data?.projects?.length === 1 ? data.projects[0].client : undefined);

	return {
		name: report?.name ?? "",
		company: (report?.company ?? null) as TReportFormValues["company"],
		client: (report?.client ??
			scopedClient ??
			null) as TReportFormValues["client"],
		workType: report?.workType
			? { code: report.workType, label: report.workType }
			: null,
		projects,
	};
};

interface IReportFormProps {
	reportId?: number;
	existingReport?: TSavedReport;
	children: ReactNode;
}

// Owns the tasks query and the Formik state for the whole report page, so
// that both the filters card (totals + generate CTA) and the body (project
// cards) read from the same form. Renders even while the tasks are loading:
// the form then simply has no projects.
export const ReportForm: FC<IReportFormProps> = ({
	reportId,
	existingReport,
	children,
}) => {
	const { filters } = useFiltersContext("reports");
	// The days a report converts minutes into are the reader's own
	// (docs/modele.md, phase 4).
	const { settings } = useUserSettings();
	const hoursPerDay = settings?.hoursPerDay ?? undefined;

	// The scope, most specific first: one project, or every project of a
	// client (docs/modele.md, phase 6).
	const scope = filters.project
		? (["project", filters.project.id] as const)
		: (["client", filters.client?.id] as const);

	const tasksQuery = useQuery({
		queryKey: reportKeys.tasks(
			scope[0],
			scope[1],
			filters.dateFrom,
			filters.dateTo
		),
		queryFn: () =>
			ReportsServices.getTasksList({
				clientId: filters.client?.id,
				projectId: filters.project?.id,
				dateFrom: filters.dateFrom,
				dateTo: filters.dateTo,
			}),
		enabled: !!(filters.project || filters.client),
	});

	// Memoized on purpose: task ids are fresh UUIDs, so recomputing on every
	// render would hand Formik (enableReinitialize) a "different" initial
	// state each time and reset the form on each render.
	const initialValues = useMemo(
		() =>
			genInitValues(
				tasksQuery.data,
				existingReport,
				hoursPerDay,
				filters.client
			),
		[tasksQuery.data, existingReport, hoursPerDay, filters.client]
	);

	const context = useMemo<IReportFormContext>(
		() => ({
			reportId,
			existingReport,
			isFinalized: !!existingReport && !existingReport.isDraft,
			tasksQuery,
		}),
		[reportId, existingReport, tasksQuery]
	);

	return (
		<ReportFormContext.Provider value={context}>
			<Formik
				initialValues={initialValues}
				onSubmit={() => {}}
				enableReinitialize
			>
				{/* Formik expects a single child when not given a render function */}
				<>{children}</>
			</Formik>
		</ReportFormContext.Provider>
	);
};
